import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPatients, deletePatient } from '../../api/patientService';
import { authService } from '../../api/authService';

// Only this admin sees the delete control.
const DELETE_ALLOWED_EMAIL = 'admin@medical.com';

const Patients = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 25,
    skip: 0,
    currentPage: 1
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [deletingId, setDeletingId] = useState(null);
  const [confirmPatient, setConfirmPatient] = useState(null);
  const canDelete =
    authService.getCurrentUser()?.email?.toLowerCase() === DELETE_ALLOWED_EMAIL;

  // Fetch patients from API
  useEffect(() => {
    loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, sortConfig.key, sortConfig.direction]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await fetchPatients({
        limit: pagination.limit,
        skip: pagination.skip,
        search: searchTerm,
        sortBy: sortConfig.key || undefined,
        sortOrder: sortConfig.direction || undefined
      });

      setPatients(result.data);
      setPagination(prev => ({
        ...prev,
        total: result.total,
        limit: result.limit,
        skip: result.skip
      }));
    } catch (err) {
      setError(err.message);
      console.error('Error loading patients:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search - triggers backend search
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, skip: 0, currentPage: 1 }));
    loadPatients();
  };

  // Handle sorting - triggers backend sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    // Reset to first page when sorting changes
    setPagination(prev => ({ ...prev, skip: 0, currentPage: 1 }));
  };

  // Get sort icon for column header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <i className="fas fa-sort text-gray-300 ml-1"></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fas fa-sort-up text-primary ml-1"></i>
      : <i className="fas fa-sort-down text-primary ml-1"></i>;
  };

  // Use patients directly - sorting is handled by backend
  const filteredPatients = patients;

  const handlePatientClick = (patientId) => {
    navigate(`/patients/${patientId}`);
  };

  // Open confirm modal for cascade-delete. Irreversible.
  const handleDeletePatient = (patient) => {
    setConfirmPatient(patient);
  };

  // Run the actual cascade delete after modal confirm.
  const confirmDelete = async () => {
    const patient = confirmPatient;
    if (!patient) return;

    try {
      setDeletingId(patient._id);
      setError(null);
      await deletePatient(patient._id);
      // Drop row locally + fix count, no full refetch needed
      setPatients(prev => prev.filter(p => p._id !== patient._id));
      setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      setConfirmPatient(null);
    } catch (err) {
      setError(err.message);
      setConfirmPatient(null);
      console.error('Error deleting patient:', err);
    } finally {
      setDeletingId(null);
    }
  };

  // Pagination handlers
  const handleNextPage = () => {
    const newSkip = pagination.skip + pagination.limit;
    if (newSkip < pagination.total) {
      setPagination(prev => ({
        ...prev,
        skip: newSkip,
        currentPage: prev.currentPage + 1
      }));
    }
  };

  const handlePrevPage = () => {
    const newSkip = Math.max(0, pagination.skip - pagination.limit);
    setPagination(prev => ({
      ...prev,
      skip: newSkip,
      currentPage: Math.max(1, prev.currentPage - 1)
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Patients</h1>
          <p className="text-neutral">
            Manage your patients and their information
            {pagination.total > 0 && (
              <span className="ml-2 text-sm">({pagination.total} total)</span>
            )}
          </p>
        </div>
       
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <i className="fas fa-exclamation-circle mr-2"></i>
            <span>{error}</span>
          </div>
          <button onClick={loadPatients} className="text-red-700 hover:text-red-900 font-semibold">
            Retry
          </button>
        </div>
      )}

      {/* Search and Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral"></i>
            <input
              type="text"
              placeholder="Search patients by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors duration-200"
          >
            <i className="fas fa-search mr-2"></i>
            Search
          </button>
         
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-neutral">Loading patients...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredPatients.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <i className="fas fa-users text-4xl text-neutral mb-4"></i>
          <h3 className="text-xl font-semibold text-primary mb-2">No Patients Found</h3>
          <p className="text-neutral">
            {searchTerm ? 'Try a different search term' : 'No patients in the system yet'}
          </p>
        </div>
      )}

      {/* Patients Table */}
      {!loading && !error && filteredPatients.length > 0 && (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <span className="flex items-center">Name {getSortIcon('name')}</span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('age')}
                  >
                    <span className="flex items-center">Age/Gender {getSortIcon('age')}</span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('emergencyContact')}
                  >
                    <span className="flex items-center">Emergency Contact {getSortIcon('emergencyContact')}</span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('createdAt')}
                  >
                    <span className="flex items-center">Created {getSortIcon('createdAt')}</span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('patientNumber')}
                  >
                    <span className="flex items-center">Patient # {getSortIcon('patientNumber')}</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light">
                {filteredPatients.map((patient) => (
                  <tr key={patient._id} className={`hover:bg-gray-50 ${patient.patientStatus === 'invited' ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-10 w-10 rounded-full ${patient.patientStatus === 'invited' ? 'bg-amber-100' : 'bg-accent/20'} flex items-center justify-center text-primary font-semibold`}>
                          {(patient.otherDetail?.name || patient.name) ? (patient.otherDetail?.name || patient.name).split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase() : '??'}
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => patient.patientStatus === 'consented' ? handlePatientClick(patient._id) : null}
                            className={`text-sm font-medium ${patient.patientStatus === 'consented' ? 'text-primary hover:text-primary-dark cursor-pointer' : 'text-neutral cursor-default'} transition-colors duration-200`}
                            disabled={patient.patientStatus === 'invited'}
                          >
                            {patient.otherDetail?.name || patient.name || 'N/A'}
                          </button>
                          <div className="text-sm text-neutral">
                            {patient.email || 'N/A'}
                            {patient.patientStatus === 'invited' && (
                              <span className="ml-2 text-amber-600 font-medium">(Invited)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-primary">{patient.otherDetail?.age || 'N/A'} years</div>
                      <div className="text-sm text-neutral">{patient.otherDetail?.sex === 'M' ? 'Male' : patient.otherDetail?.sex === 'F' ? 'Female' : 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-primary">{patient.otherDetail?.emergencyName || patient.emergencyName || 'N/A'}</div>
                      <div className="text-sm text-neutral">{patient.otherDetail?.emergencyNumber || patient.emergencyNumber || patient.otherDetail?.phoneNumber || patient.phoneNumber || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-neutral">{formatDate(patient.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className="text-sm text-neutral truncate max-w-[120px]"
                        title={patient.patientNumber ? `#${patient.patientNumber}` : 'N/A'}
                      >
                        #{patient.patientNumber
                          ? (String(patient.patientNumber).length > 12
                              ? String(patient.patientNumber).slice(0, 12) + '...'
                              : patient.patientNumber)
                          : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {patient.patientStatus === 'consented' ? (
                        <button
                          onClick={() => handlePatientClick(patient._id)}
                          className="text-primary hover:text-primary-dark mr-3"
                          title="View details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      ) : (
                        <span className="text-neutral/40 mr-3" title="Cannot view - invitation pending">
                          <i className="fas fa-eye-slash"></i>
                        </span>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDeletePatient(patient)}
                          disabled={deletingId === patient._id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={patient.patientStatus === 'invited' ? 'Delete invite' : 'Delete patient and all records'}
                        >
                          {deletingId === patient._id
                            ? <i className="fas fa-spinner fa-spin"></i>
                            : <i className="fas fa-trash"></i>}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="bg-white rounded-lg shadow px-6 py-4 mt-4 flex items-center justify-between">
            <div className="text-sm text-neutral">
              Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} patients
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePrevPage}
                disabled={pagination.skip === 0}
                className="px-4 py-2 border border-light rounded-lg text-neutral hover:bg-light transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-chevron-left mr-2"></i>
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={pagination.skip + pagination.limit >= pagination.total}
                className="px-4 py-2 border border-light rounded-lg text-neutral hover:bg-light transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <i className="fas fa-chevron-right ml-2"></i>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {confirmPatient && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => deletingId ? null : setConfirmPatient(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-6 pt-6">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-primary">
                  {confirmPatient.patientStatus === 'invited' ? 'Delete Invite' : 'Delete Patient'}
                </h3>
                <p className="text-sm text-neutral">This action cannot be undone</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              <p className="text-sm text-primary mb-3">
                Permanently delete{' '}
                <span className="font-semibold">
                  {confirmPatient.otherDetail?.name || confirmPatient.name || confirmPatient.email || 'this record'}
                </span>
                {confirmPatient.email && (confirmPatient.otherDetail?.name || confirmPatient.name) && (
                  <span className="text-neutral"> ({confirmPatient.email})</span>
                )}
                ?
              </p>
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <p className="text-xs text-red-700 font-medium mb-1">
                  The following will be permanently removed:
                </p>
                {confirmPatient.patientStatus === 'invited' ? (
                  <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5">
                    <li>Pending invite record</li>
                    <li>Invitation token (patient can no longer accept)</li>
                  </ul>
                ) : (
                  <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5">
                    <li>Patient record</li>
                    <li>Attack events &amp; medication logs</li>
                    <li>Refills &amp; clinical notes</li>
                    <li>Notifications &amp; invites</li>
                    <li>Linked mobile app account</li>
                  </ul>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50">
              <button
                onClick={() => setConfirmPatient(null)}
                disabled={!!deletingId}
                className="px-4 py-2 border border-light rounded-lg text-neutral hover:bg-light transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!!deletingId}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {deletingId ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash mr-2"></i>
                    {confirmPatient.patientStatus === 'invited' ? 'Delete Invite' : 'Delete Patient'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Patients;
